/*  *****************************************************

Common drag and drop functions for draggable components
These methods will be proxied on each component

*****************************************************   */
import Vue from "vue";
import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";

/**
 * Function callback - placeholder that will be used later
 */
export function onComponentDragEnter() {}

/**
 * Function callback called when the element starts dragging
 * It will set the effectAllowed and will set id of element being moved
 * It will make the element semi-transparent while being dragged
 * @param {Object} ev - Start Drag Event
 */
export function onComponentStartDrag(ev) {
  if (this) {
    ev.dataTransfer.effectAllowed = this.dropEffect;
    ev.dataTransfer.setData("component.move", this.id);
    this.$el.classList.add("m-drag_start");
    // Location of this element to check if it was dropped into a different location
    this.offsetTop = this.$el.offsetTop;
    this.offsetLeft = this.$el.offsetLeft;
    dragAndDrop.componentStartDragging(this);
  }
}

/**
 * Fuction callback when drag ends, it will reset the transparency
 */
export function onComponentEndDrag() {
  this.$el.classList.remove("m-drag_start");
  if (
    this.$el.offsetTop !== this.offsetTop ||
    this.$el.offsetLeft !== this.offsetLeft
  ) {
    // element was dropped into a different location
    dragAndDrop.updateIsDirtyFlag({ isDirty: true });
  }
  dragAndDrop.componentEndDragging(this);
}

export function onCanvasDropzone(ev) {
  this.onComponentDrop(ev);
}
/**
 * Function callback to either add a new component or move existing component
 * @param {Object} ev - Drop Event
 */
export function onComponentDrop(ev) {
  dragAndDrop.componentDropzone(ev, this);
}

/**
 * Function callback add a new component into the canvas if allowed
 * @param {Object} obj - Object with information to create new modal or edit
 * E.g. {"modal":{"id":"123","create":"ButtonComponent","editElementId":null},"props":{}}
 * if editElementId is not null then it will call update component function.
 */
export function onModalCreate(obj) {
  const id = obj.modal.id;
  this.removeModal(id);
  if (obj.modal.editElementId) {
    this.updateComponent(obj);
  }
}

/**
 * Function callback edit existing component
 * @param {Object} obj - Object with information to edit
 * E.g. {"modal":{"id":"123","create":"ButtonComponent","editElementId":"774895"},"props":{}}
 */
export function updateComponent(obj) {
  for (const [key, value] of Object.entries(obj.props)) {
    this[key] = value;
  }
  dragAndDrop.updateIsDirtyFlag({ isDirty: true });
  dragAndDrop.triggerCanvasUpdate();
}

/**
 * Function to show edit modal.
 * @param {String} modal - Name of modal
 * @param {Object} editData - Object with props to edit component
 */
export function showEditModal(modal, editData) {
  if (!this.$refs.modals) {
    const modals = document.createElement("div");
    this.$el.appendChild(modals);
    this.$refs["modals"] = modals;
  }
  const onToggle = obj => {
    this.removeModal(obj.id);
  };
  const onCreate = obj => {
    this.onModalCreate(obj);
  };

  const instance = dragAndDrop.createModal(modal, onToggle, onCreate, editData);
  if (instance) {
    this.$refs.modals.appendChild(instance.$el);
    this.$refs[instance.id] = instance;
  } else {
    console.error(`Modal component for ${modal} not added to the list`);
  }
}

export function removeModal(id) {
  if (id && this.$refs[id]) {
    this.$refs[id].show = false;
    const oldInstance = this.$refs[id];
    this.$refs.modals.removeChild(oldInstance.$el);
    delete this.$refs[id];
  }
}

/**
 * Function callback that is triggered when the gear icon is clicked.
 * If this component contains the element then it will trigger the edit modal
 * @param {Object} obj - Object that contains existing data from component.
 */
export function onElementEdit(obj) {
  if (obj.id === this.id) {
    this.showEditModal(obj.modal, obj);
  }
}
export function onDeleteComponent(obj) {
  for (const [id, component] of Object.entries(this.$refs)) {
    if (obj.id === id) {
      this.$refs.canvas.removeChild(component.$el);
      if (component.destroyChildren) {
        component.destroyChildren();
      }
      delete this.$refs[id];
      component.$destroy();
      if (this.id.indexOf("card-layout-") === 0) {
        this.normalizeElements();
      }
      dragAndDrop.updateIsDirtyFlag({ isDirty: true });
      dragAndDrop.triggerCanvasUpdate(true);
      return true;
    }
  }
}

export function removeDropzoneLines(dropPointPrefix) {
  for (const [id, child] of Object.entries(this.$refs)) {
    if (child instanceof Vue && child.id.indexOf(dropPointPrefix) === 0) {
      this.$el.removeChild(child.$el);
      child.$destroy();
      delete this.$refs[id];
    }
  }
  // do some extra cleanup if any left over lines
  const elArr = [];
  for (const el of this.$el.childNodes) {
    if (
      el.getAttribute &&
      el.getAttribute("id") &&
      el.getAttribute("id").indexOf(dropPointPrefix) === 0
    ) {
      elArr.push(el);
    }
  }
  for (const el of elArr) {
    this.$el.removeChild(el);
  }
}
